import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PortfolioFilters = ({ onFilterChange, activeFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const filterCategories = [
    {
      id: 'industry',
      label: 'Industry',
      icon: 'Building2',
      options: [
        { value: 'all', label: 'All Industries' },
        { value: 'ecommerce', label: 'E-commerce' },
        { value: 'saas', label: 'SaaS' },
        { value: 'fintech', label: 'FinTech' },
        { value: 'media', label: 'Media & News' },
        { value: 'marketplace', label: 'Marketplace' }
      ]
    },
    {
      id: 'stage',
      label: 'Investment Stage',
      icon: 'TrendingUp',
      options: [
        { value: 'all', label: 'All Stages' },
        { value: 'seed', label: 'Seed' },
        { value: 'series-a', label: 'Series A' },
        { value: 'series-b', label: 'Series B' },
        { value: 'growth', label: 'Growth' },
        { value: 'exit', label: 'Exited' }
      ]
    },
    {
      id: 'location',
      label: 'Location',
      icon: 'MapPin',
      options: [
        { value: 'all', label: 'All Locations' },
        { value: 'germany', label: 'Germany' },
        { value: 'europe', label: 'Europe' },
        { value: 'north-america', label: 'North America' },
        { value: 'global', label: 'Global' }
      ]
    },
    {
      id: 'performance',
      label: 'Performance',
      icon: 'Award',
      options: [
        { value: 'all', label: 'All Performance' },
        { value: 'high-growth', label: 'High Growth' },
        { value: 'profitable', label: 'Profitable' },
        { value: 'unicorn', label: 'Unicorn Track' },
        { value: 'ipo-ready', label: 'IPO Ready' }
      ]
    }
  ];

  const handleFilterSelect = (category, value) => {
    onFilterChange(category, value);
  };

  const clearAllFilters = () => {
    filterCategories?.forEach(category => {
      onFilterChange(category?.id, 'all');
    });
  };

  const hasActiveFilters = Object.values(activeFilters)?.some(filter => filter !== 'all');

  return (
    <section className="bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-2xl font-bold text-foreground">Portfolio Companies</h2>
            <div className="hidden sm:flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Filter" size={16} />
              <span>Filter by category</span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                iconName="X"
                iconPosition="left"
                className="text-muted-foreground hover:text-foreground"
              >
                Clear All
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
              iconPosition="right"
              className="lg:hidden"
            >
              Filters
            </Button>
          </div>
        </div>

        {/* Desktop Filters */}
        <div className="hidden lg:grid grid-cols-4 gap-6">
          {filterCategories?.map((category) => (
            <div key={category?.id} className="space-y-3">
              <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                <Icon name={category?.icon} size={16} />
                <span>{category?.label}</span>
              </div>
              
              <div className="space-y-2">
                {category?.options?.map((option) => (
                  <button
                    key={option?.value}
                    onClick={() => handleFilterSelect(category?.id, option?.value)}
                    className={`w-full text-left px-3 py-2 rounded-brand-sm text-sm transition-brand ${
                      activeFilters?.[category?.id] === option?.value
                        ? 'bg-primary text-primary-foreground shadow-brand-sm'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    {option?.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Filters */}
        <div className={`lg:hidden transition-brand overflow-hidden ${
          isExpanded ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="space-y-6 pt-4">
            {filterCategories?.map((category) => (
              <div key={category?.id} className="space-y-3">
                <div className="flex items-center space-x-2 text-sm font-medium text-foreground">
                  <Icon name={category?.icon} size={16} />
                  <span>{category?.label}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  {category?.options?.map((option) => (
                    <button
                      key={option?.value}
                      onClick={() => handleFilterSelect(category?.id, option?.value)}
                      className={`text-left px-3 py-2 rounded-brand-sm text-sm transition-brand ${
                        activeFilters?.[category?.id] === option?.value
                          ? 'bg-primary text-primary-foreground shadow-brand-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                      }`}
                    >
                      {option?.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground mb-3">
              <Icon name="Filter" size={16} />
              <span>Active filters:</span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters)?.map(([category, value]) => {
                if (value === 'all') return null;
                
                const categoryData = filterCategories?.find(c => c?.id === category);
                const optionData = categoryData?.options?.find(o => o?.value === value);
                
                return (
                  <div
                    key={`${category}-${value}`}
                    className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm"
                  >
                    <span>{optionData?.label}</span>
                    <button
                      onClick={() => handleFilterSelect(category, 'all')}
                      className="hover:bg-primary/20 rounded-full p-1 transition-brand"
                    >
                      <Icon name="X" size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PortfolioFilters;